import type { InventoryChangeEntry } from 'src/admin/models/PoolManagementModel';
import { escapeCsvCell } from 'src/admin/models/PoolManagementModel/upload/csvEscape';

export interface ChangeLogCsvColumnLabels {
    timestamp: string;
    user: string;
    symbol: string;
    direction: string;
    quantity: string;
    price: string;
    orderId: string;
}

export interface CreateChangeLogCsvOptions {
    /**
     * Human labels for the header row. Pulled out as a parameter (rather
     * than hard-coded English) so the caller can localize via i18n
     * without re-importing this module.
     */
    columnLabels: ChangeLogCsvColumnLabels;
    /**
     * Formats `entry.timestamp` (ms-since-epoch) into a display string.
     * Defaults to ISO-8601 UTC so the file opens predictably in Excel /
     * Sheets regardless of locale; callers that prefer the user's local
     * format can pass their own.
     */
    formatTimestamp?: (timestampMs: number) => string;
}

/**
 * Serialize a list of `InventoryChangeEntry` rows to a CSV string.
 *
 * Pure: no clock reads, no DOM access, no i18n imports. The caller
 * supplies the localized header labels (and optionally a timestamp
 * formatter) so the builder stays trivially unit-testable and the
 * data shape — `direction` derived from the quantity sign — matches
 * the view's sign-based filter (PLAT-12442).
 */
export function createChangeLogCsv(
    entries: ReadonlyArray<InventoryChangeEntry>,
    options: CreateChangeLogCsvOptions,
): string {
    const { columnLabels, formatTimestamp = defaultFormatTimestamp } = options;
    const header = [
        escapeCsvCell(columnLabels.timestamp),
        escapeCsvCell(columnLabels.user),
        escapeCsvCell(columnLabels.symbol),
        escapeCsvCell(columnLabels.direction),
        escapeCsvCell(columnLabels.quantity),
        escapeCsvCell(columnLabels.price),
        escapeCsvCell(columnLabels.orderId),
    ].join(',');
    const lines = [header];
    for (const entry of entries) {
        lines.push(
            [
                escapeCsvCell(formatTimestamp(entry.timestamp)),
                escapeCsvCell(entry.userName),
                escapeCsvCell(entry.symbol),
                escapeCsvCell(directionLabel(entry.quantity)),
                // Negative quantities mean a decrease; record the
                // absolute magnitude so spreadsheets show the same
                // number that the UI's "Decreased by N" label reads.
                escapeCsvCell(Math.abs(entry.quantity)),
                escapeCsvCell(entry.price),
                escapeCsvCell(entry.orderId ?? ''),
            ].join(','),
        );
    }
    return lines.join('\n') + '\n';
}

function defaultFormatTimestamp(ms: number): string {
    if (!Number.isFinite(ms)) return '';
    // `Number.isFinite` lets through values outside the legal `Date`
    // range (~±100M days from epoch). Those produce a `Date` whose
    // time value is `NaN`, and `toISOString()` then throws
    // `RangeError`, which would break the whole export. Guard
    // explicitly so a single bad timestamp downgrades to an empty cell.
    const date = new Date(ms);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString();
}

function directionLabel(quantity: number): string {
    if (quantity > 0) return 'increase';
    if (quantity < 0) return 'decrease';
    return 'neutral';
}
